import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, formatErr } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, User, Unlink, ChevronRight } from "lucide-react";
import { getDeviceToken, clearDeviceToken } from "./TabletEntry";

export default function TabletHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const dt = getDeviceToken();

  useEffect(() => {
    if (!dt) { navigate("/tablet"); return; }
    // If a user is already logged in, log them out so tablet flow can start fresh.
    if (user) { logout(); }
    (async () => {
      try {
        const r = await api.get(`/tablet/session?device_token=${encodeURIComponent(dt)}`);
        setSession(r.data);
      } catch (e) {
        toast.error(formatErr(e) || "Tablet non associato");
        clearDeviceToken();
        navigate("/tablet");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const pickUser = (op) => {
    navigate(`/tablet/pin/${op.user_id}`, { state: { operaio: op, session } });
  };

  const unpair = () => {
    if (!window.confirm("Rimuovere l'associazione di questo tablet al furgone?")) return;
    clearDeviceToken();
    navigate("/tablet");
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-950 flex items-center justify-center">
      <Loader2 className="w-10 h-10 text-white animate-spin"/>
    </div>
  );

  if (!session) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white flex flex-col">
      <header className="p-4 flex items-center justify-between border-b border-white/10 bg-black/20 backdrop-blur safe-top safe-x">
        <div className="flex items-center gap-3">
          <img src="/assets/logo.png" alt="Energy World" className="w-11 h-11 rounded-xl bg-white p-1"/>
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-200">Furgone</div>
            <div className="heading text-xl font-bold" data-testid="tablet-furgone-name">{session.furgone_name}</div>
          </div>
        </div>
        <button onClick={unpair} className="text-blue-200 hover:text-white text-xs inline-flex items-center gap-1" data-testid="tablet-unpair-btn">
          <Unlink className="w-3.5 h-3.5"/> Disassocia
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <h1 className="heading text-3xl md:text-4xl font-extrabold mb-2">Chi sei?</h1>
        <p className="text-blue-200 mb-8 text-sm">Tocca la tua card per iniziare a lavorare</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 w-full max-w-3xl" data-testid="tablet-operai-grid">
          {session.operai.length === 0 && (
            <div className="col-span-full text-center text-blue-200 text-sm bg-white/5 border border-white/10 rounded-xl p-8">
              Nessun operaio associato a questo furgone.<br/>
              Chiedi all'amministratore di aggiungere gli operai.
            </div>
          )}
          {session.operai.map(op => (
            <button
              key={op.user_id}
              onClick={() => pickUser(op)}
              className="group bg-white/10 hover:bg-white/20 active:bg-white/25 backdrop-blur-md border border-white/10 rounded-2xl p-6 flex flex-col items-center gap-3 transition transform active:scale-95 min-h-[220px]"
              data-testid={`tablet-op-card-${op.user_id}`}>
              {op.picture ? (
                <img src={op.picture} alt={op.name} className="w-24 h-24 rounded-full object-cover border-2 border-white/40" referrerPolicy="no-referrer"/>
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 flex items-center justify-center text-white text-3xl font-bold">
                  {(op.name || "?").split(" ").map(x => x[0]).slice(0, 2).join("").toUpperCase()}
                </div>
              )}
              <div className="text-center">
                <div className="heading text-xl font-bold">{op.name}</div>
                {op.worker_category && <div className="text-blue-200 text-xs mt-1">{op.worker_category}{op.worker_subcategory ? ` · ${op.worker_subcategory}` : ""}</div>}
              </div>
              <div className="mt-auto text-blue-100 text-xs inline-flex items-center gap-1 opacity-70 group-hover:opacity-100">
                {op.has_pin ? "Inserisci PIN" : "Primo accesso"} <ChevronRight className="w-4 h-4"/>
              </div>
            </button>
          ))}
        </div>
      </main>

      <footer className="p-4 text-center text-blue-300/60 text-[11px] border-t border-white/5">
        Energy World · Impianti Tecnologici · Orzinuovi (BS)
      </footer>
    </div>
  );
}
